import { faSearch, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import CategoryChips from '../CategoryChips/CategoryChips';
import Filters, { defaultFilters } from '../Filters/Filters';
import useTypewriter from '../../hooks/useTypewriter';

// True once any filter has moved off its default value — drives the toggle's
// active-state badge so it's obvious a filter is on even while collapsed.
const hasActiveFilters = (filters) =>
    !!filters && Object.keys(defaultFilters).some((key) => filters[key] !== defaultFilters[key]);

/**
 * Compact, sticky search toolbar for the gallery routes (Photos / Illustrations /
 * Videos). Rendered as a floating card that mirrors the navbar pill above it —
 * same max width, rounding, border and shadow — so the two bars align and read
 * as one system. Search box + inline filter pills share row 1; the quick-category
 * chips sit in a single horizontally-scrollable row beneath. The card sticks just
 * below the floating navbar on every screen size.
 *
 * Note: this component breaks out of its parent's `max-w-7xl` padding via the
 * negative margins, then re-centers its own content at `max-w-6xl` to match the
 * navbar exactly.
 */
const SearchPicture = ({
    value,
    onChange,
    filters,
    setFilters,
    showColor = true,
    showOrientation = true,
    words = ['photos', 'illustrations', 'videos'],
    // Accessible name for the search box. The placeholder animates, so it can't
    // serve as the label — screen readers would read a half-typed word.
    label = 'Search media',
}) => {
    const typed = useTypewriter(words);
    const [isOpen, setIsOpen] = useState(false);
    const filtersActive = hasActiveFilters(filters);

    return (
        <div className="sticky top-[4.5rem] z-30 -mx-4 sm:-mx-6 lg:-mx-8 mb-6 px-3 sm:px-4 pt-2">
            <div className="max-w-6xl mx-auto rounded-3xl bg-white/80 dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 shadow-e2 backdrop-blur-xl px-4 sm:px-5 py-3.5">
                {/* row 1 — search + filters toggle */}
                <div className="flex items-center gap-3">
                    {/* No vertical padding here — the input carries it, so the
                        input itself (not just the pill) is a 44px-tall target. */}
                    <div className="group relative flex items-center flex-1 min-w-0 bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-full shadow-e0 focus-within:bg-white dark:focus-within:bg-gray-900 focus-within:ring-2 focus-within:ring-brand-400 focus-within:border-brand-400 px-4 transition-all">
                        <FontAwesomeIcon icon={faSearch} className="text-gray-400 group-focus-within:text-brand-500 shrink-0 transition-colors" />
                        {/* focus:outline-none is deliberate here: the wrapper's
                            focus-within ring is the focus indicator, so an
                            outline on the bare input would double it up. */}
                        <input
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            type="text"
                            aria-label={label}
                            className="bg-transparent appearance-none border-none text-gray-700 dark:text-gray-100 dark:placeholder-gray-500 w-full min-h-[44px] px-2 leading-tight focus:outline-none"
                            placeholder={`Search for ${typed}`}
                        />
                        {value &&
                            <button
                                type="button"
                                onClick={() => onChange('')}
                                aria-label="Clear search"
                                className="tap-target inline-flex items-center justify-center h-8 w-8 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/70 dark:hover:bg-gray-700 shrink-0 transition-colors"
                            >
                                &times;
                            </button>
                        }
                    </div>

                    <button
                        type="button"
                        onClick={() => setIsOpen((prev) => !prev)}
                        aria-expanded={isOpen}
                        aria-label="Toggle filters and categories"
                        className={`tap-target relative shrink-0 inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border text-sm font-medium transition-colors ${
                            isOpen
                                ? 'bg-brand-500 border-brand-500 text-white'
                                : 'bg-gray-50 dark:bg-gray-900/60 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400'
                        }`}
                    >
                        <FontAwesomeIcon icon={faSlidersH} />
                        <span className="hidden sm:inline">Filters</span>
                        {filtersActive &&
                            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white dark:ring-gray-800" />
                        }
                    </button>
                </div>

                {/* row 2 — inline filters + quick categories, collapsed until toggled */}
                <div className={`grid transition-[grid-template-rows] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] mt-3' : 'grid-rows-[0fr]'}`}>
                    <div className="overflow-hidden">
                        {filters && setFilters &&
                            <Filters
                                filters={filters}
                                setFilters={setFilters}
                                showColor={showColor}
                                showOrientation={showOrientation}
                            />
                        }
                        <CategoryChips
                            active={value}
                            onSelect={onChange}
                            fadeClass="from-white dark:from-gray-800"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SearchPicture;
